import { style, selection, color } from "d3";
import { type, data } from "jquery";
import { size, result } from "lodash";
import { useState, useEffect, useContext } from "react";
import ReactDatePicker from "react-datepicker";
import {
  Button,
  Dropdown,
  DropdownItemProps,
  Form,
  FormProps,
  Input,
  Modal,
  Segment,
  Select,
  Table,
  TextArea,
} from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl, useRequest, useTableSort } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { formatEnumName, hostUrl, restRequest } from "../http";
import { ENUM_TYPES_ISSUE_OVERVIEW_ROOT_CAUSE, PageData } from "../models/common_model";
import { DropdownOption } from "../models/testing_model";
import { Config, IssueOverview } from "../models/issue_overview_model";
import { formatDateTime } from "../utils";
import { JournalizationComponent } from "./commons/JournalizationComponent";
import { Page } from "./commons/Page";
import { IssueOverviewEditor } from "./IssueOverviewEditor";
import _ from "lodash";
import { IssueOverviewUnitsAffectedSelector } from "./IssueOverviewUnitsAffected";
import { LewittProjectDropdown } from "./commons/LewittProject";
import { LanguageContext } from "../context";

export function IssueOverviewComponent(props: {}) {
  const currentUser = useCurrentUser();

  const [searchData, setSearchField] = useFormData<any>({
    product: "",
    station: "",
    order: "",
    measurement_status: [1, 2, 3],
    updated_status: [0, 1, 2],
    project_id: -1,
    testing_id: -1,
  });
  
  const [sort, sortBy] = useTableSort<IssueOverview>('id', 2);

  useEffect(() => {
    const sortInfo: any = {};
    if (sort.field && sort.dir) {
      sortInfo.sorted_field = sort.field;
      sortInfo.sorted_direction = sort.dir;
    } else {
      sortInfo.sorted_field = null;
      sortInfo.sorted_direction = null;
    }

    setSearchData((sd: any) => ({ ...sd, ...sortInfo }));
  }, [sort]);
  
  const [summaries, setSearchData, searchLoading] = useFormConnect<
    any,
    PageData<IssueOverview>
  >({}, "issue_overview/search");

  const editModalControl = useModalControl();
  const [currentEditId, setCurrentEditId] = useState<number>();
  const journalizationModalControl = useModalControl();
  const [currentJournalizationId, setCurrentJournalizationId] =
    useState<number>();
  const [reloadNumber, setReloadNumber] = useState(0);

  const [configs] = useRequest<Config>('issue_overview/configs', undefined, 'get');
  

  return (
    <>
      <Segment.Group>
        <Segment attached="top">
          <Form
            onSubmit={(e) => setSearchData(searchData)}
            size="mini"
            className="list-search"
            loading={searchLoading}
          >
            <Form.Group>
              <Form.Field inline>
                <Input
                  placeholder="ID"
                  name="id"
                  value={searchData["id"] || ""}
                  onChange={setSearchField}
                  autoComplete="off"
                />
              </Form.Field>

              <Form.Input inline label="Project:" style={{ minWidth: "10rem" }}>
                <LewittProjectDropdown emptyText="All" value={searchData.product} onChange={p => setSearchField(null, { name: 'product', value: p })} />
              </Form.Input>

              <Form.Field inline>
                <Input
                  placeholder="Name"
                  name="name"
                  value={searchData["name"] || ""}
                  onChange={setSearchField}
                  autoComplete="off"
                />
              </Form.Field>

              <Form.Input label="Status:" inline>
                <Dropdown fluid selection name="status" value={searchData["status"] || -1} onChange={setSearchField} options={[{key: 'all', value: -1, text: '-All-'}, ...(configs?.statuses.map((s, i) => ({key: s, text: s, value: i + 1})) || [])]}></Dropdown>
              </Form.Input>

              <Form.Field inline>
                <Input
                  placeholder="Failure Description"
                  name="description"
                  value={searchData["description"] || ""}
                  onChange={setSearchField}
                  autoComplete="off"
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder="Units Affected"
                  name="serial_numbers"
                  value={searchData["serial_numbers"] || ""}
                  onChange={setSearchField}
                  autoComplete="off"
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder="Measurement Fail"
                  name="fail_measurements"
                  value={searchData["fail_measurements"] || ""}
                  onChange={setSearchField}
                  style={{ width: '15rem' }}
                  autoComplete="off"
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder="Remark"
                  name="remark"
                  value={searchData["remark"] || ""}
                  onChange={setSearchField}
                  autoComplete="off"
                />
              </Form.Field>

              <div>
                <Button icon="search" type="submit" size="mini"></Button>

                <Button
                  as="a"
                  icon="file excel outline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      hostUrl(
                        "issue_overview/export?search=" +
                          encodeURI(JSON.stringify(searchData))
                      ),
                      "_blank"
                    );
                  }}
                  size="mini"
                ></Button>
              </div>
            </Form.Group>
          </Form>
        </Segment>
        <Segment attached="bottom">
          <Table striped sortable selectable style={{width: '100%', tableLayout: 'fixed'}}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell {...sortBy('id')}>ID</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('product')}>Project</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('name')}>Issue Name</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('build_name')}>Buildname</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('description')}>Failue Description</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('serial_numbers')}>Unit Affected</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('fail_measurements')}>Measurement Fail</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('analysis')}>Failure Analysis</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('root_cause')}>Root Cause</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('actions')}>Corrective Actions</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('resolved_by')}>Resolved By</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('created_at')}>Created At</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('author')}>Reported By</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('owner_name')}>Owner</Table.HeaderCell>
                <Table.HeaderCell  {...sortBy('status')}>Status</Table.HeaderCell>
                <Table.HeaderCell>
                  {currentUser.permissions.issue_overview.create && (
                    <Button
                      color="blue"
                      icon="add"
                      content="Issue Overview"
                      size="mini"
                      onClick={(e) => {
                        setCurrentEditId(undefined);
                        editModalControl.setOpen(true);
                      }}
                    />
                  )}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {summaries?.items.map((obj) => {
                return (
                  <tr key={obj.id}>
                    <td>{obj.id || ""}</td>
                    <td>{obj.product || ""}</td>
                    <td>{obj.name}</td>
                    <td>{obj.build_name}</td>
                    <td dangerouslySetInnerHTML={{__html: `<div>${obj.description || ''}</div>` }} className="td-3line"></td>
                    <td dangerouslySetInnerHTML={{__html: `<div>${obj.serial_numbers || ''}</div>` }} className="td-3line"></td>
                    <td>{obj.fail_measurements.join(', ')}</td>
                    <td dangerouslySetInnerHTML={{__html: `<div>${obj.analysis || ''}</div>` }} className="td-3line"></td>
                    <td dangerouslySetInnerHTML={{__html: `<div>${obj.root_cause || ''}</div>` }} className="td-3line"></td>
                    <td dangerouslySetInnerHTML={{__html: `<div>${obj.actions || ''}</div>` }} className="td-3line"></td>
                    <td>{obj.resolved_by?.name || ''}</td>
                    <td>{formatDateTime(obj.created_at)}</td>
                    <td>{obj.author?.name}</td>
                    <td>{obj.owner_name || ''}</td>
                    <td>{configs?.statuses[obj.status - 1]}</td>
                    <td>
                      {currentUser.permissions.issue_overview.update && (
                        <Button
                          title="Edit"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentEditId(obj.id);
                            editModalControl.setOpen(true);
                          }}
                          color="blue"
                          icon="edit"
                          size="mini"
                        ></Button>
                      )}

                      {currentUser.permissions.issue_overview.delete && (
                        <Button
                          onClick={(e) => {
                            dialogControl.deletionConfirm(
                              obj.name,
                              (argee) => {
                                if (argee) {
                                  return restRequest(
                                    `issue_overview/${obj.id}`,
                                    null,
                                    "delete"
                                  ).then(() => {
                                    setSearchData((searchData: any) => ({
                                      ...searchData,
                                      refresh: (searchData.refresh || 1) + 1,
                                    }));
                                  });
                                }
                                return Promise.resolve();
                              }
                            );
                          }}
                          color="red"
                          icon="delete"
                          size="mini"
                        />
                      )}

                      <Button
                        title="Journalization"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentJournalizationId(obj.id);
                          journalizationModalControl.setOpen(true);
                        }}
                        icon="history"
                        size="mini"
                      ></Button>
                    </td>
                  </tr>
                );
              })}
            </Table.Body>
          </Table>
          <Page data={summaries} updatePage={setSearchData}></Page>
        </Segment>
      </Segment.Group>

      <IssueOverviewEditor
        id={currentEditId}
        modelControl={editModalControl}
        onFinish={(result) => {
          setSearchData((searchData: any) => ({
            ...searchData,
            refresh: (searchData.refresh || 1) + 1,
          }));
          editModalControl.setOpen(false);
          setReloadNumber((n) => n + 1);
        }}
      ></IssueOverviewEditor>

      {currentJournalizationId && (
        <Journalization
          id={currentJournalizationId}
          modelControl={journalizationModalControl}
        />
      )}
    </>
  );
}

function Journalization(props: { id: number; modelControl: ModalControlData }) {
  const [configs] = useRequest<Config>('issue_overview/configs', undefined, 'get');
  const [t, lang] = useContext(LanguageContext);
  

  return (
    <Modal {...props.modelControl.props} size="fullscreen">
      <Modal.Header>Journalization</Modal.Header>
      <Modal.Content>
        <JournalizationComponent
          containerId={props.id}
          containerType={8}
          detailFields={[
            { key: "name", name: "Name" },
            { key: "build_name", name: "Buildname" },
            {
              key: "description",
              name: "Description",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: 'serial_numbers',
              name: "Units Affected",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: "fail_measurements",
              name: "Measurements that FAIL the units",
              isList: true
            },
            {
              key: "analysis",
              name: "Analysis",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: "root_cause",
              name: "Root Cause",
              formater: (v) => formatEnumName(ENUM_TYPES_ISSUE_OVERVIEW_ROOT_CAUSE, v, lang)
            },
            {
              key: "actions",
              name: "Corrective Actions",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
            {
              key: "status",
              name: "Status",
              formater: (v) => {
                return configs?.statuses[v - 1] || '';
              },
            },
            {
              key: "owner_id",
              name: "Owner",
              formater: (v) => v ? restRequest<string>('users/user_name/'+v, null, 'get') : ''
            },
            {
              key: "resolved_by_id",
              name: "Resolved By",
              formater: (v) => v ? restRequest<string>('users/user_name/'+v, null, 'get') : ''
            },
            {
              key: "files",
              name: "Attachment",
              isList: true,
              formater: (v) => v["name"],
            },
            {
              key: "remark",
              name: "Remark",
              formater: (v) =>
                v ? <div dangerouslySetInnerHTML={{ __html: v }}></div> : v,
            },
          ]}
        />
      </Modal.Content>
    </Modal>
  );
}
